import { useEffect, useMemo, useRef, useState } from "react";
import { getParticipantLayoutDialogMediaSnapshot } from "./participant-layout-dialog-media";

const DIALOG_MEDIA_POLL_INTERVAL_MS = 500;

function getAppObject() {
  if (typeof window !== "undefined") {
    return window.APP;
  }

  if (typeof global !== "undefined") {
    return global.APP;
  }

  return undefined;
}

function getParticipantLayoutDialogMediaStateKey(state) {
  const cameraStateKey = Object.keys(state.cameraTrackKeysByOwnerId || {})
    .sort()
    .map(ownerId => `${ownerId}:${state.cameraTrackKeysByOwnerId[ownerId]}`)
    .join(",");
  const screenShareStateKey = (state.screenShares || [])
    .map(screenShare => `${screenShare.id}:${screenShare.ownerId}:${screenShare.trackKey}`)
    .join(",");

  return [
    state.localMicEnabled === null ? "unknown" : state.localMicEnabled ? "mic-on" : "mic-off",
    cameraStateKey || "no-camera-tracks",
    screenShareStateKey || "no-screen-shares"
  ].join(":");
}

export function useParticipantLayoutDialogMediaState({ enabled = true, mySessionId } = {}) {
  const app = getAppObject();
  const [state, setState] = useState(() =>
    getParticipantLayoutDialogMediaSnapshot({
      dialog: app?.dialog,
      mySessionId
    })
  );
  const stateKeyRef = useRef(getParticipantLayoutDialogMediaStateKey(state));

  useEffect(() => {
    const syncState = () => {
      const nextState = getParticipantLayoutDialogMediaSnapshot({
        dialog: app?.dialog,
        mySessionId
      });
      const nextStateKey = getParticipantLayoutDialogMediaStateKey(nextState);

      if (nextStateKey !== stateKeyRef.current) {
        stateKeyRef.current = nextStateKey;
        setState(nextState);
      }
    };

    syncState();

    if (!enabled) {
      return;
    }

    app?.dialog?.on?.("stream_updated", syncState);
    app?.dialog?.on?.("mic-state-changed", syncState);

    const intervalId = setInterval(syncState, DIALOG_MEDIA_POLL_INTERVAL_MS);

    return () => {
      app?.dialog?.off?.("stream_updated", syncState);
      app?.dialog?.off?.("mic-state-changed", syncState);
      clearInterval(intervalId);
    };
  }, [app?.dialog, enabled, mySessionId]);

  return useMemo(
    () => ({
      localMicEnabled: state.localMicEnabled,
      cameraMediaStreamsByOwnerId: state.cameraMediaStreamsByOwnerId,
      screenShares: state.screenShares || [],
      screenShareActive: Boolean((state.screenShares || []).length),
      screenShareOwnerId: state.shareOwnerId,
      screenShareMediaStream: state.shareMediaStream
    }),
    [
      state.cameraMediaStreamsByOwnerId,
      state.localMicEnabled,
      state.screenShares,
      state.shareMediaStream,
      state.shareOwnerId
    ]
  );
}
